// src/resources/dashboards/Painel360/EngagementSection.tsx
import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { stringify } from 'query-string';
import { usePermissions } from 'react-admin';
import { PERMISSIONS } from '../../../constants';
import { COLORS, EmptyHint, ErrorCard, SectionHeader, SkeletonCard, cardSx, fmtBrl, fmtNum, fmtPct, openInNewTab } from './shared';
import { EngagementData, SectionState } from './types';

// Taxa pequena (ex.: read rate ~0,4%) não arredonda pra "0%" — mostra 1 casa.
const pctSmall = (v: number) => {
  if (!isFinite(v) || v <= 0) return '0%';
  const p = v * 100;
  return p < 1 ? `${p.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%` : fmtPct(v);
};

// `action` opcional: botão dentro do card, na linha do título — mesmo padrão
// que PenetrationSection.tsx usa pro "Ver detalhes por posto" (o botão fica
// solto no cabeçalho da seção quando não está dentro do quadro que ele abre).
const Card = ({ title, hint, action, children }: { title: string; hint: string; action?: React.ReactNode; children: React.ReactNode }) => (
  <Box sx={cardSx}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.ink }}>{title}</Typography>
      {action}
    </Box>
    <Typography sx={{ fontSize: 12, color: COLORS.muted, mb: 1.5 }}>{hint}</Typography>
    {children}
  </Box>
);

const Stat = ({ label, value, color }: { label: string; value: string; color?: string }) => (
  <Box>
    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: COLORS.faint }}>{label}</Typography>
    <Typography sx={{ fontSize: 22, fontWeight: 800, color: color ?? COLORS.ink }}>{value}</Typography>
  </Box>
);

const FunnelStep = ({ label, value, pctOfFirst, color, textColor }: {
  label: string; value: string; pctOfFirst: number; color: string; textColor?: string;
}) => {
  // Barra estreita (< ~22%) não cabe o texto dentro → mostra o valor FORA, em cor escura,
  // pra não ficar branco-sobre-claro clipado (caso "Lidas" com poucos %).
  const wide = pctOfFirst >= 0.22;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.9 }}>
      <Typography sx={{ width: 96, fontSize: 12, color: COLORS.muted, fontWeight: 600, textAlign: 'right' }}>{label}</Typography>
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(100, Math.max(6, pctOfFirst * 100))}%` }} transition={{ duration: 0.9 }}
          style={{ height: 26, borderRadius: 7, background: color, flexShrink: 0, display: 'flex', alignItems: 'center', paddingLeft: wide ? 10 : 0, minWidth: wide ? undefined : 8 }}>
          {wide && <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: textColor ?? '#fff', whiteSpace: 'nowrap' }}>{value}</Typography>}
        </motion.div>
        {!wide && <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: COLORS.ink, whiteSpace: 'nowrap' }}>{value}</Typography>}
      </Box>
    </Box>
  );
};

interface EngagementSectionProps {
  state: SectionState<EngagementData>;
  // Mesmo período que gerou os dados desta seção — só pra montar o link de
  // drill-down do card de push (react-admin lê `filter` da query string).
  // `from`/`to` chegam CRUS ('YYYY-MM-DD', estado do cabeçalho); openPushDetails
  // converte pra borda certa antes de navegar (mesma convenção do PenetrationSection).
  from: string;
  to: string;
  chainId: string;
  // Postos selecionados no cabeçalho — só pra montar o atalho "Ver comentários"
  // (manda `stationId` pro Voz do Cliente quando exatamente 1 posto está filtrado).
  stationIds: string[];
}

const EngagementSection = ({ state, from, to, chainId, stationIds }: EngagementSectionProps) => {
  const { permissions } = usePermissions();

  if (state.loading) return <SkeletonCard height={380} />;
  if (state.error) return <ErrorCard onRetry={state.refetch} height={380} />;
  if (!state.data) return null;
  const { push, club, recovery, drawing, roleta, referral, ratings } = state.data;

  const openPushDetails = () => {
    // `from` no começo do dia e `to` no FIM do dia, em ISO — mandar o `to` cru
    // deixaria o dia inteiro de fora (o backend trata `to` como exclusivo).
    const filter = {
      from: dayjs(from).startOf('day').toISOString(),
      to: dayjs(to).endOf('day').toISOString(),
      ...(chainId ? { chainId } : {}),
    };
    openInNewTab(`/report-push-notifications?${stringify({ filter: JSON.stringify(filter) })}`);
  };

  // Voz do Cliente lê `from`/`to`/`stationId` crus da URL como estado inicial
  // (mesma convenção INCLUSIVA dos filtros de data de lá) — nunca converter pra ISO aqui.
  const canSeeComments = !!permissions?.includes(PERMISSIONS.COMMENT_ANALYSIS);
  const openComments = () => {
    openInNewTab(`/dashboard-comments?${stringify({
      from,
      to,
      stationId: stationIds.length === 1 ? stationIds[0] : undefined,
    })}`);
  };

  const sentimentTotal = ratings.sentiment.positive + ratings.sentiment.neutral + ratings.sentiment.negative;
  // Barras do clube são proporcionais ao MAIOR valor (não é funil monotônico:
  // cupons gerados pode ser > conquistas — vários cupons por conquista).
  const clubMax = club ? Math.max(1, club.achievements, club.couponsGenerated, club.redeemed, club.expired) : 1;

  return (
    <Box>
      <SectionHeader
        icon={<RocketLaunchIcon sx={{ fontSize: 18 }} />}
        gradient={`linear-gradient(135deg, #ec4899, ${COLORS.purple})`}
        title="Ferramentas de engajamento"
        subtitle="Push, clube de vantagens, recuperação, sorteios e avaliações — o que está funcionando"
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: 'repeat(3, 1fr)' }, gap: 1.75 }}>

        <Card
          title="📲 Notificações push"
          hint={`Funil de entrega · ${fmtNum(push.messages)} mensagens no período`}
          action={(
            <Button
              size="small"
              variant="outlined"
              onClick={openPushDetails}
              endIcon={<ArrowForwardIcon fontSize="small" />}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Ver detalhes
            </Button>
          )}
        >
          {!push.sent ? <EmptyHint text={push.dispatches ? `${fmtNum(push.dispatches)} disparos — entrega ainda não consolidada.` : 'Nenhum disparo no período.'} /> : (
            <Box>
              <FunnelStep label="Enviadas" value={fmtNum(push.sent)} pctOfFirst={1} color={COLORS.brand} />
              <FunnelStep label="Recebidas" value={`${fmtNum(push.received)} · ${pctSmall(push.received / push.sent)}`} pctOfFirst={push.received / push.sent} color={COLORS.purple} />
              <FunnelStep label="Lidas" value={`${fmtNum(push.read)} · ${pctSmall(push.read / push.sent)}`} pctOfFirst={push.read / push.sent} color="#a78bfa" />
              <FunnelStep label="Clicadas" value={`${fmtNum(push.clicked)} · ${pctSmall(push.clicked / push.sent)}`} pctOfFirst={push.clicked / push.sent} color="#c4b5fd" textColor="#4c1d95" />
            </Box>
          )}
        </Card>

        {club && (
          <Card title="🎁 Clube de Vantagens" hint="Funil de cupons do período">
            {!club.achievements && !club.couponsGenerated ? <EmptyHint text="Nenhuma conquista no período." /> : (
              <Box>
                <FunnelStep label="Conquistas" value={fmtNum(club.achievements)} pctOfFirst={club.achievements / clubMax} color={COLORS.teal} />
                <FunnelStep label="Cupons gerados" value={fmtNum(club.couponsGenerated)} pctOfFirst={club.couponsGenerated / clubMax} color="#2dd4bf" />
                <FunnelStep label="Resgatados" value={`${fmtNum(club.redeemed)} · ${club.couponsGenerated ? fmtPct(club.redeemed / club.couponsGenerated) : '—'}`} pctOfFirst={club.redeemed / clubMax} color="#5eead4" textColor="#0f766e" />
                <FunnelStep label="Expirados" value={fmtNum(club.expired)} pctOfFirst={club.expired / clubMax} color="#fca5a5" textColor="#991b1b" />
              </Box>
            )}
          </Card>
        )}

        {recovery && (
          <Card title="🔄 Recuperação de inativos" hint="Programa automático da rede">
            <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
              <Stat label="Inscritos" value={fmtNum(recovery.enrolled)} />
              <Stat label="Recuperados" value={`${fmtNum(recovery.recovered)}${recovery.recoveredPct !== null ? ` · ${fmtPct(recovery.recoveredPct)}` : ''}`} color={COLORS.success} />
              <Stat label="Custo" value={fmtBrl(recovery.cost)} />
              <Stat label="Receita pós-volta" value={fmtBrl(recovery.revenueAfterRecovery)} color={COLORS.brand} />
            </Box>
            {recovery.cost > 0 && (
              <Typography sx={{ fontSize: 12, color: COLORS.muted, mt: 1.5 }}>
                Cada R$ 1 em desconto de recuperação trouxe <b style={{ color: COLORS.ink }}>{fmtBrl(recovery.revenueAfterRecovery / recovery.cost)}</b> de volta
              </Typography>
            )}
          </Card>
        )}

        {drawing && (
          <Card title="🎟️ Sorteios" hint={drawing.name}>
            <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
              <Stat label="Participantes" value={fmtNum(drawing.participants)} />
              <Stat label="Números gerados" value={fmtNum(drawing.numbers)} />
              <Stat label="% da base ativa" value={fmtPct(drawing.pctOfActiveBase)} color={COLORS.brand} />
            </Box>
          </Card>
        )}

        {roleta && (
          <Card title="🎡 Roleta da Sorte" hint={roleta.name}>
            {!roleta.granted ? <EmptyHint text="Nenhum giro concedido no período." /> : (
              <Box>
                <FunnelStep label="Concedidos" value={fmtNum(roleta.granted)} pctOfFirst={1} color={COLORS.brand} />
                <FunnelStep
                  label="Girados"
                  value={`${fmtNum(roleta.spun)} · ${fmtPct(roleta.spun / roleta.granted)}`}
                  pctOfFirst={roleta.spun / roleta.granted}
                  color={COLORS.purple}
                />
                <FunnelStep
                  label="Prêmios"
                  value={`${fmtNum(roleta.prizes)}${roleta.prizeRate !== null ? ` · ${fmtPct(roleta.prizeRate)}` : ''}`}
                  pctOfFirst={roleta.prizes / roleta.granted}
                  color="#c4b5fd"
                  textColor="#4c1d95"
                />
                <FunnelStep
                  label="Expirados"
                  value={fmtNum(roleta.expired)}
                  pctOfFirst={roleta.expired / roleta.granted}
                  color={COLORS.danger}
                />
                <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mt: 1.25 }}>
                  <Stat label="Clientes" value={fmtNum(roleta.participants)} />
                  <Stat label="Custo dos prêmios" value={fmtBrl(roleta.prizeCost)} />
                </Box>
                {roleta.topSegment && (
                  <Typography sx={{ fontSize: 12, color: COLORS.muted, mt: 1 }}>
                    Fatia que mais saiu: <b style={{ color: COLORS.ink }}>{roleta.topSegment.label}</b>{' '}
                    ({fmtNum(roleta.topSegment.count)}x)
                  </Typography>
                )}
              </Box>
            )}
          </Card>
        )}

        {referral && (
          <Card title="🤝 Indicações" hint="Cliente chama cliente">
            <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
              <Stat label="Indicados no período" value={fmtNum(referral.referred)} />
              <Stat label="Já abasteceram" value={`${fmtNum(referral.converted)}${referral.referred ? ` · ${fmtPct(referral.converted / referral.referred)}` : ''}`} color={COLORS.success} />
            </Box>
          </Card>
        )}

        <Card
          title="⭐ Avaliações"
          hint={`${fmtNum(ratings.count)} avaliações no período${ratings.analyzed > 0 ? ' · sentimento dos comentários por IA' : ''}`}
          action={canSeeComments ? (
            <Button
              size="small"
              variant="outlined"
              onClick={openComments}
              endIcon={<ArrowForwardIcon fontSize="small" />}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Ver comentários
            </Button>
          ) : undefined}
        >
          {!ratings.count ? <EmptyHint text="Nenhuma avaliação no período." /> : (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography sx={{ fontSize: 34, fontWeight: 800, color: COLORS.ink }}>{fmtNum(ratings.average, 1)}</Typography>
                <Box>
                  <Typography sx={{ color: COLORS.warning, fontSize: 18, letterSpacing: 2 }}>
                    {'★'.repeat(Math.round(ratings.average ?? 0))}{'☆'.repeat(5 - Math.round(ratings.average ?? 0))}
                  </Typography>
                  {ratings.previousAverage !== null && ratings.average !== null && (
                    <Typography sx={{ fontSize: 11.5, color: COLORS.muted }}>
                      {ratings.average >= ratings.previousAverage ? '▲' : '▼'} {fmtNum(Math.abs(ratings.average - ratings.previousAverage), 1)} vs período anterior
                    </Typography>
                  )}
                </Box>
              </Box>
              {sentimentTotal > 0 ? (
                <>
                  <Box sx={{ display: 'flex', height: 14, borderRadius: 1.5, overflow: 'hidden', mt: 1.25 }}>
                    <Box sx={{ width: `${(ratings.sentiment.positive / sentimentTotal) * 100}%`, bgcolor: COLORS.success }} />
                    <Box sx={{ width: `${(ratings.sentiment.neutral / sentimentTotal) * 100}%`, bgcolor: COLORS.faint }} />
                    <Box sx={{ width: `${(ratings.sentiment.negative / sentimentTotal) * 100}%`, bgcolor: COLORS.danger }} />
                  </Box>
                  <Typography sx={{ fontSize: 11.5, color: COLORS.muted, mt: 0.75 }}>
                    Positivos {fmtPct(ratings.sentiment.positive / sentimentTotal)} · Neutros {fmtPct(ratings.sentiment.neutral / sentimentTotal)} · Negativos {fmtPct(ratings.sentiment.negative / sentimentTotal)}
                    {ratings.requiresAttention > 0 && <> · <b style={{ color: COLORS.danger }}>{fmtNum(ratings.requiresAttention)} exigem atenção</b></>}
                  </Typography>
                </>
              ) : ratings.withComment > 0 ? (
                <Typography sx={{ fontSize: 11.5, color: COLORS.faint, mt: 1.25 }}>
                  {fmtNum(ratings.withComment)} comentários no período — sentimento ainda não analisado pela IA.
                </Typography>
              ) : null}
            </Box>
          )}
        </Card>
      </Box>
    </Box>
  );
};

export default EngagementSection;
